import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import parking from 'src/assets/images/parking.svg';
import ClientsTables from 'src/features/clients/components/ClientsTables';
import { clientsMessages } from 'src/features/clients/utils/messages';
import { useListMemberships } from 'src/features/membership/api/useListMemberships';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useGetCurrentCloudUser } from 'src/features/membershipCloudUsers/api/useGetCurrentCloudUser';
import { useGetMembershipDetailed } from 'src/features/membershipCloudUsers/api/useGetMembershipDetailed';
import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { zoneRuleStayTypeBadgeOptions } from 'src/features/membershipType/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { Badge } from 'src/registry/ui/Badge';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

const formatTime = (date: string) => date.slice(0, 5) ?? '';

export default function ClientLayout() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: userMemberships } = useListMemberships({ parkingId });
    const membershipId = userMemberships?.results ? userMemberships?.results[0]?.id : '';

    const { data: membershipDetails } = useGetMembershipDetailed({
        parkingId,
        membershipId,
        config: { enabled: !!membershipId },
    });
    const { data: currentCloudUser } = useGetCurrentCloudUser({
        parkingId,
        membershipId,
        config: { enabled: !!membershipId },
    });
    const cloudUserAccessLevel = currentCloudUser?.access_level;

    const translatedZones = React.useMemo(
        () =>
            membershipDetails?.membership_zone_rules?.map((zone) => {
                const translatedZone = zone.zone.translations.find((z) => z.language === locale);

                return translatedZone?.name;
            }),
        [membershipDetails?.membership_zone_rules, locale],
    );

    const translatedGateways = React.useMemo(
        () =>
            membershipDetails?.membership_type.restricted_gateways?.map((gateway) => {
                const translatedGateway = gateway.translations?.find((g) => g.language === locale);

                return translatedGateway?.name;
            }),
        [membershipDetails?.membership_type.restricted_gateways, locale],
    );

    const gateways =
        translatedGateways && translatedGateways.length > 0
            ? translatedGateways?.map((z) => z).join(', ')
            : formatMessage(clientsMessages.allGateways);

    const zoneRules = membershipDetails?.membership_zone_rules ?? [];

    return (
        <>
            <div className="flex flex-col gap-9">
                <div className="flex gap-9">
                    <GeneralBlock className="flex-1 items-center justify-center">
                        <img src={parking} alt="Parking" />
                    </GeneralBlock>
                    <GeneralBlock className="grid flex-3 grid-cols-3">
                        <GeneralBlockAttribute
                            label={formatMessage(clientsMessages.validFrom)}
                            value={getFormattedDateTimeForDetailPanel(membershipDetails?.valid_from)}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(clientsMessages.validTo)}
                            value={getFormattedDateTimeForDetailPanel(membershipDetails?.valid_to)}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(clientsMessages.allowedParkingSpaces)}
                            value={membershipDetails?.max_allowed_spaces_count ?? '-'}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(clientsMessages.allowedZones)}
                            value={zoneRules.length > 0 ? translatedZones?.map((z) => z).join(', ') : '-'}
                        />

                        <GeneralBlockAttribute
                            label={formatMessage(clientsMessages.allowedGateways)}
                            value={gateways}
                        />
                    </GeneralBlock>
                </div>

                <div className="flex gap-9">
                    <GeneralBlock className="flex max-h-max flex-1 flex-col">
                        <GeneralBlockAttribute label={formatMessage(clientsMessages.allowedDays)}>
                            <div className="mt-2 w-full">
                                {Object.values(DAY_OF_WEEK).map((day: DAY_OF_WEEK) => {
                                    const membershipTypeHasTimeRestrictions = Boolean(
                                        membershipDetails?.membership_type.has_time_restriction,
                                    );

                                    const membershipAllowedTimes = membershipDetails?.membership_allowed_times.find(
                                        (time) => time.day_of_week === day,
                                    );
                                    const membershipTypeAllowedTimes =
                                        membershipDetails?.membership_type?.membership_type_allowed_times?.find(
                                            (time) => time.day_of_week === day,
                                        );

                                    const allowedTimesToUse = membershipAllowedTimes
                                        ? membershipAllowedTimes
                                        : membershipTypeAllowedTimes;

                                    const weekDay = formatMessage(membershipMessages[day]);

                                    let allowedTimes;
                                    if (!membershipTypeHasTimeRestrictions) {
                                        allowedTimes = '00:00 - 23:59';
                                    } else if (allowedTimesToUse) {
                                        allowedTimes = `${formatTime(allowedTimesToUse.from_time)} - ${formatTime(allowedTimesToUse.to_time)}`;
                                    } else {
                                        allowedTimes = (
                                            <div className="text-red-600">
                                                {formatMessage(clientsMessages.notAllowedDay)}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div
                                            className="mt-2 flex flex-col gap-2 xl:flex-row xl:justify-between xl:gap-0.5"
                                            key={day}
                                        >
                                            <div>{weekDay}</div>
                                            <div className="flex gap-2">{allowedTimes}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </GeneralBlockAttribute>

                        <GeneralBlockAttribute label={formatMessage(clientsMessages.allowedZones)}>
                            <div className="mt-2 w-full">
                                {zoneRules.length > 0 ? (
                                    membershipDetails?.membership_zone_rules.map((zone) => {
                                        const badgeIcon =
                                            zone.allowed_stay_type &&
                                            zoneRuleStayTypeBadgeOptions[zone.allowed_stay_type].icon;
                                        const translatedZone = zone.zone.translations.find(
                                            (z) => z.language === locale,
                                        )?.name;
                                        const zoneAllowedSpaces = zone.allowed_spaces_count ?? 0;

                                        return (
                                            <div
                                                className="mt-2 flex flex-col gap-2 xl:flex-row xl:justify-between xl:gap-0.5"
                                                key={zone.id}
                                            >
                                                <div>{`${translatedZone} (${zoneAllowedSpaces})`}</div>
                                                {zone.allowed_stay_type && (
                                                    <div>
                                                        <Badge
                                                            theme={
                                                                zoneRuleStayTypeBadgeOptions[zone.allowed_stay_type]
                                                                    .theme
                                                            }
                                                        >
                                                            {badgeIcon && <FontAwesomeIcon icon={badgeIcon} />}
                                                            {formatMessage(
                                                                zoneRuleStayTypeBadgeOptions[zone.allowed_stay_type]
                                                                    .label,
                                                            )}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div>-</div>
                                )}
                            </div>
                        </GeneralBlockAttribute>
                    </GeneralBlock>

                    <ClientsTables membershipId={membershipId} accessLevel={cloudUserAccessLevel} />
                </div>
            </div>
        </>
    );
}
